import { Loader2, UserCheck } from "lucide-react";
import React, { useState } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { ProfileMatchResultData } from "./ProfileMatchResult";

interface ProfileMatchButtonProps {
  jobUrl: string;
  onMatchResult: (result: ProfileMatchResultData) => void;
  authToken: string;
}

export const ProfileMatchButton: React.FC<ProfileMatchButtonProps> = ({
  jobUrl,
  onMatchResult,
  authToken,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCheckMatch = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Scrape job to get job_id
      const scrapeResponse = await fetch(API_ENDPOINTS.COMPANY_SCRAPPER.SCRAPE_JOB, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ URL: jobUrl }),
      });

      if (!scrapeResponse.ok) {
        if (scrapeResponse.status === 403) {
          setError("Quota exceeded. Please upgrade your subscription.");
          return;
        }
        throw new Error("Failed to load job information");
      }

      const scrapeData = await scrapeResponse.json();
      if (!scrapeData.success || !scrapeData.id) {
        throw new Error("Failed to get job information");
      }

      const jobId = scrapeData.id;

      // Step 2: Call profile match service
      const matchResponse = await fetch(API_ENDPOINTS.PROFILE_MATCH.MATCH, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ job_id: jobId }),
      });

      if (!matchResponse.ok) {
        if (matchResponse.status === 403) {
          setError("Quota exceeded. Please upgrade your subscription.");
          return;
        }
        throw new Error("Failed to analyze profile match");
      }

      const matchData = await matchResponse.json();
      if (!matchData.success) {
        throw new Error(matchData.error || "Failed to analyze profile match");
      }

      onMatchResult({
        match_percentage: matchData.match_percentage,
        requirements: matchData.requirements,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <button
        onClick={handleCheckMatch}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-jobesta-blue text-white rounded-jobesta hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Analyzing...</span>
          </>
        ) : (
          <>
            <UserCheck className="w-4 h-4" />
            <span>Check Profile Match</span>
          </>
        )}
      </button>
      {error && (
        <p className="text-sm text-red-600 text-center">
          {error}
        </p>
      )}
    </div>
  );
};

export default ProfileMatchButton;
